// src/api/refusal.ts
// EVERY WAY THE SERVER CAN SAY NO, AS A CLOSED SET — and never as a crash.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THIS MODULE EXISTS AT ALL
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The backend went to real trouble to make its refusals machine-readable and self-describing:
//
//   · the kill switch (src/practice/kill-switch.ts) answers 503 with `switch_name` and a `message`
//     already written for a human, precisely so a client does not have to compose a vaguer one;
//   · the upload lane (src/practice/upload-service.ts) answers with a closed `reason` enum and,
//     when the deployment is unconfigured, the LIST of environment variables that are missing;
//   · the gateway catch-all answers unmounted paths with `unmounted: true` and `route_not_mounted`,
//     built so a probe "cannot distinguish 'this route does not exist' from 'this route exists and
//     refused you'" is no longer possible.
//
// A client that collapses all of that into "Something went wrong. Try again." throws away every bit
// of it — and, worse, it turns a DELIBERATE STOP into what looks like a bug. The whole reason the
// upload lane exists is that its predecessor returned `{configured: false, upload_url: null}` and a
// client read the presence of a field as success. This module is the client-side half of that fix:
// a refusal is parsed into a named kind, and the server's own sentence is carried to the screen
// verbatim. Nothing here invents an error message on the server's behalf.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE ONE RULE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `classify()` is total. Every HTTP response that is not 2xx, and every transport failure, maps to
// exactly one `RefusalKind`. There is no `default: throw`. An unrecognised shape becomes
// `unknown_refusal` carrying the raw body — visible and ugly, which is correct, because a refusal
// we do not understand is exactly the thing that must not be smoothed over.

/** The closed set. A screen switches on this; it never regex-matches an error string. */
export type RefusalKind =
  /** 401 — no token, empty token, or the token did not verify. */
  | 'unauthenticated'
  /** 403 — verified, but not this athlete's owner / verified guardian / authorized coach. */
  | 'forbidden'
  /** 404 + `unmounted: true` — THIS BUILD DOES NOT HAVE THE ROUTE. Not a permission problem. */
  | 'not_mounted'
  /** 404 without `unmounted` — the row does not exist, or is not yours to know exists. */
  | 'not_found'
  /** 409 — the request was understood and refused on its merits (e.g. `confirmed` was not true). */
  | 'conflict'
  /** 400 — malformed. Carries the upload lane's `reason` when there is one. */
  | 'invalid_request'
  /** 503 `practice_lab_unavailable` — a person turned the Practice Lab off. `switch_name` says who. */
  | 'lab_stopped'
  /** 503 `r2_not_configured` — video storage is not set up on this deployment. `missing` lists the vars. */
  | 'upload_not_configured'
  /** 503 `bucket_env_conflict` / `public_base_url_has_path` — storage is configured WRONGLY. */
  | 'upload_misconfigured'
  /** 503 anything else — a dependency is unavailable. */
  | 'service_unavailable'
  /** 5xx — the server broke. This one really is a fault. */
  | 'server_error'
  /** The request never reached a server: no network, DNS, TLS, or CORS refusal. */
  | 'unreachable'
  /** A non-2xx we could not place. Carries the raw body so it is visible rather than swallowed. */
  | 'unknown_refusal';

export interface Refusal {
  readonly ok: false;
  readonly kind: RefusalKind;
  /** HTTP status, or 0 when the request never reached a server. */
  readonly status: number;
  /**
   * THE SENTENCE TO PUT ON THE SCREEN. Taken from the server's own `message` or `error` whenever it
   * sent one — see the header block. Only synthesised for `unreachable`, where by definition there
   * is no server sentence to carry.
   */
  readonly message: string;
  /** The server's machine-readable sub-reason: the upload lane's `reason`, or the flag decision's. */
  readonly reason: string | null;
  /** Which env switch decided, on a kill-switch 503. `null` for the in-process kill latch. */
  readonly switchName: string | null;
  /** Environment variables the deployment is missing, on `upload_not_configured`. */
  readonly missing: readonly string[];
  /** The gateway's extra paragraph ("This is a deliberate stop, not a fault."), when it sent one. */
  readonly note: string | null;
  /** The parsed body, verbatim, for the details disclosure. Never rendered as the headline. */
  readonly body: unknown;
  /** Method + path, so a refusal can be shown next to what provoked it. */
  readonly request: string;
}

/** Is this refusal one that retrying later could plausibly fix? Drives the offline queue. */
export function isTransient(r: Refusal): boolean {
  switch (r.kind) {
    case 'unreachable':
    case 'server_error':
    case 'service_unavailable':
      return true;
    // A stopped lab and an unconfigured bucket are transient in the sense that a PERSON can fix
    // them — but not by the client retrying in ten seconds. They are held, not spun on.
    case 'lab_stopped':
    case 'upload_not_configured':
    case 'upload_misconfigured':
    case 'not_mounted':
      return false;
    default:
      return false;
  }
}

/**
 * Does this refusal mean "a human with access to the deployment must do something"?
 *
 * Kept separate from `isTransient` because the QUEUE and the SCREEN want different answers. The
 * queue asks "should I retry this?" (no). The screen asks "is this the user's fault?" (also no —
 * and telling them to try again would be a lie).
 */
export function needsOperator(r: Refusal): boolean {
  return r.kind === 'lab_stopped'
    || r.kind === 'upload_not_configured'
    || r.kind === 'upload_misconfigured'
    || r.kind === 'not_mounted';
}

/**
 * Will retrying THIS EXACT REQUEST ever produce a different answer?
 *
 * ── WHY THIS IS WIDER THAN needsOperator() (26 Aug 2026) ──────────────────────────────────────
 * A queued job's payload is FROZEN at enqueue time — that is the whole basis of the idempotency
 * guarantee, and it is correct. But it means a 400 `invalid_request` is permanent: the body the
 * server called malformed is the only body this job will ever send.
 *
 * The queue nevertheless treated 400 as `failed`, which `drain()` re-sends on every pass. The
 * founder's screen showed one such job at **84 attempts** — a session minted with the invented mode
 * `NET_PRACTICE` before that was fixed, re-sent on every drain, every one of them refused for the
 * identical reason, and each press of "Send everything runnable" adding more.
 *
 * `needsOperator` means "someone with access to the DEPLOYMENT must act" — configure R2, turn the
 * lab back on. That is not this. Nobody can repair a frozen payload; the job has to be removed and
 * a new one created. Both are permanent, so both stop the retry loop, but they ask for different
 * actions and the UI should not tell someone to go and configure a bucket.
 */
export function neverSucceedsOnRetry(r: Refusal): boolean {
  return needsOperator(r) || r.kind === 'invalid_request';
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

function asRecord(v: unknown): Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * Turn a non-2xx response body into a `Refusal`.
 *
 * TOTAL by construction — see THE ONE RULE. `body` is whatever was parsed from the response; it may
 * be a string (the server sent HTML or plain text), an object, or `undefined` (empty body).
 */
export function classify(status: number, body: unknown, request: string): Refusal {
  const b = asRecord(body);
  const reason = str(b['reason']);
  const code = str(b['code']);
  // The kill switch and the upload lane both write `message`; the generic `fail()` helper writes
  // only `error`. Prefer the one written for a human, then fall back.
  const serverSentence = str(b['message']) ?? str(b['error']) ?? (typeof body === 'string' ? str(body) : null);
  const missing = Array.isArray(b['missing']) ? (b['missing'] as unknown[]).map(String) : [];
  const note = str(b['note']);
  const switchName = typeof b['switch_name'] === 'string' ? (b['switch_name'] as string) : null;

  const base = {
    ok: false as const,
    status,
    reason,
    switchName,
    missing,
    note,
    body,
    request,
  };

  const make = (kind: RefusalKind, fallback: string): Refusal =>
    ({ ...base, kind, message: serverSentence ?? fallback });

  // ── 503 first: it is the status this product uses to say "deliberately stopped", and three
  // different subsystems say it for three different reasons that must not be merged.
  if (status === 503) {
    // src/practice/kill-switch.ts — the whole /v1/practice namespace is off.
    if (str(b['error']) === 'practice_lab_unavailable' || code === 'practice_lab_unavailable') {
      return make('lab_stopped', 'The Practice Lab is switched off on this deployment.');
    }
    if (reason === 'r2_not_configured') {
      return make('upload_not_configured', 'Video upload storage is not configured on this deployment.');
    }
    if (reason === 'bucket_env_conflict' || reason === 'public_base_url_has_path') {
      return make('upload_misconfigured', 'Video upload storage is configured incorrectly on this deployment.');
    }
    return make('service_unavailable', 'A service this screen depends on is unavailable.');
  }

  // ── 404 that is the GATEWAY saying the route is not in this build, which is NOT a permission
  // answer and must never be shown as one. The backend built this distinction deliberately.
  if (status === 404 && (b['unmounted'] === true || code === 'route_not_mounted')) {
    return make('not_mounted', 'This build of the server does not have that route.');
  }

  switch (status) {
    case 400: return make('invalid_request', 'The server rejected the request as malformed.');
    case 401: return make('unauthenticated', 'You are not signed in, or your session has expired.');
    case 403: return make('forbidden', 'You are not allowed to see this.');
    case 404: return make('not_found', 'Not found.');
    case 409: return make('conflict', 'The server refused this request.');
    default: break;
  }
  if (status >= 500) return make('server_error', 'The server failed while handling this request.');
  return make('unknown_refusal', `The server answered ${status} in a shape this app does not recognise.`);
}

/**
 * A transport failure — the request never reached a server.
 *
 * DELIBERATELY NOT CALLED "offline". A `fetch` rejection on a browser is indistinguishable from a
 * CORS refusal, and CORS is a live risk here: the gateway's allowlist middleware fails closed, so an
 * origin missing from `ALLOWED_ORIGINS` looks exactly like no network. Claiming "you are offline"
 * when the truth is "this origin is not allowlisted" would send someone hunting for signal for an
 * hour. The wording names both possibilities; `navigator.onLine` narrows it where it can.
 */
export function unreachable(request: string, cause: unknown, online: boolean | null = null): Refusal {
  const detail = cause instanceof Error ? cause.message : String(cause ?? 'no detail');
  const message = online === false
    ? 'No network connection. Nothing was sent.'
    : 'Could not reach the server. This is either a network problem or the browser blocked the '
      + 'request (the API allows only origins listed in ALLOWED_ORIGINS). Nothing was sent.';
  return {
    ok: false,
    kind: 'unreachable',
    status: 0,
    message,
    reason: null,
    switchName: null,
    missing: [],
    note: null,
    body: { transport_error: detail, navigator_online: online },
    request,
  };
}
